import { RewardsType, RewardsValue } from 'app/console-api/apps/types';
import styled from 'styled-components';
import { BodyText, Button, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import React, { useMemo } from 'react';
import { DropdownValue } from '@make-software/cspr-design/dist/lib/components/dropdown/dropdown';
import {
  DropdownStyles,
  StyledDismissButton,
  StyledInput
} from 'app/components/common/common-styles';
import Select from 'react-select';
import DeleteBinIcon from 'assets/icons/ic-delete-bin.svg';

const StyledAddReward = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    width: '115px',
    cursor: 'pointer',
    fontWeight: '400',
    lineHeight: '24px',
    marginBottom: '48px',
    '&:hover': {
      color: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

const RewardsDescription = styled.div(({ theme }) =>
  theme.withMedia({
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '20px',
    display: 'block',
    color: theme.styleguideColors.contentSecondary
  })
);

const StyledRewardRow = styled(FlexRow)`
  width: 100%;
  gap: 5px;
  align-items: flex-start;
`;

const StyledRewardColumn = styled.div`
  flex: 1;
`;

const StyledRemoveColumn = styled.div`
  width: 86px;
`;

export const StyledConfirmButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%'],
    backgroundColor: theme.styleguideColors.fillPrimaryBlue,
    ':hover': {
      background: theme.styleguideColors.fillPrimaryBlueHover
    },
    ':active': {
      background: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

export interface RewardsValueControlProps {
  rewardValues: RewardsValue[];
  rewardTypes: RewardsType[];
  onChange: (rewards: RewardsValue[]) => void;
}

export const RewardValueControl = ({
  rewardValues,
  rewardTypes,
  onChange
}: RewardsValueControlProps) => {
  const rewardDropdownValues = useMemo<DropdownValue[]>(
    () => rewardTypes.map((t) => ({ label: t.name, value: t.id.toString() })),
    [rewardTypes]
  );

  const handleRewardsValueChange = (index: number, value: number) => {
    const updated = rewardValues.map((r, i) => (i === index ? { ...r, value } : r));
    onChange(updated);
  };

  const handleRewardsTypeChange = (index: number, one: DropdownValue) => {
    const updated = rewardValues.map((r, i) =>
      i === index ? { ...r, rewardID: Number(one.value) } : r
    );
    onChange(updated);
  };

  const addReward = () => {
    if (rewardValues.length >= 3 || rewardTypes.length === 0) return;
    onChange([...rewardValues, { rewardID: rewardTypes[0].id, value: 100 }]);
  };

  const removeReward = (index: number) => {
    onChange(rewardValues.filter((_, i) => i !== index));
  };

  return (
    <>
      <FlexColumn gap={4}>
        <BodyText size={1}>Rewards:</BodyText>
        <RewardsDescription>
          Select the rewards types and amounts to reward users successfully claimed for completing
          the task.
        </RewardsDescription>
      </FlexColumn>
      <FlexColumn gap={10}>
        {/* Header Row */}
        <FlexRow justify={'space-between'} style={{ width: '100%' }}>
          <FlexColumn style={{ flex: 2 }}>
            <BodyText size={1}>Type</BodyText>
          </FlexColumn>
          <FlexColumn style={{ flex: 1 }}>
            <BodyText size={1}>Amount</BodyText>
          </FlexColumn>
          <StyledRemoveColumn></StyledRemoveColumn>
        </FlexRow>
        {rewardValues.map((reward, index) => (
          <FlexRow key={index} gap={5} align={'center'}>
            <StyledRewardRow key={index}>
              <FlexColumn style={{ flex: 2 }}>
                <Select
                  value={rewardDropdownValues.find(
                    (value) => value.value === reward.rewardID.toString()
                  )}
                  onChange={(value) =>
                    handleRewardsTypeChange(index, value as unknown as DropdownValue)
                  }
                  options={rewardDropdownValues}
                  styles={DropdownStyles()}
                />
              </FlexColumn>
              <StyledRewardColumn style={{ flex: 1 }}>
                <StyledInput
                  label={''}
                  placeholder="coin"
                  value={reward.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleRewardsValueChange(index, Number(e.target.value))
                  }
                />
              </StyledRewardColumn>
              <StyledRemoveColumn>
                {index !== 0 && (
                  <StyledDismissButton
                    color={'utility'}
                    height={'36'}
                    onClick={() => removeReward(index)}
                  >
                    <SvgIcon src={DeleteBinIcon} size={16} />
                  </StyledDismissButton>
                )}
              </StyledRemoveColumn>
            </StyledRewardRow>
          </FlexRow>
        ))}
      </FlexColumn>
      <StyledAddReward
        size={3}
        scale={'sm'}
        variation={rewardValues.length === 3 ? 'gray' : 'blue'}
      >
        <div onClick={addReward}> + Add Reward</div>
      </StyledAddReward>
    </>
  );
};
